// LT-611 — public "Get called by our AI now" demo widget (GTM §7).
//
// Lives under src/pages/LandingPage/ so it shares the marketing visual language
// (and the dark-theme / DS-token lint allowlist). Rendered both inline on the
// landing page (LandingDemoSection) and on the standalone public /demo page.
//
// A visitor picks a scenario (preset) or types their own topic, then either
// gets a PSTN call on their phone or talks to the agent in-browser (WebRTC).
import React, { useMemo, useState } from 'react';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';

import { useDemoCall } from './useDemoCall';

const CATEGORY_LABELS: Record<string, string> = {
  collections_payments: 'Collections & payments',
  sales_retention: 'Sales & retention',
  operations: 'Operations',
  insurance_financial: 'Insurance & financial',
  education: 'Education',
};

type Channel = 'phone' | 'web';

interface DemoCallWidgetProps {
  className?: string;
}

const DemoCallWidget: React.FC<DemoCallWidgetProps> = ({ className }) => {
  const demo = useDemoCall();
  const [channel, setChannel] = useState<Channel>('phone');
  const [presetKey, setPresetKey] = useState<string>('');
  const [freeText, setFreeText] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);

  const grouped = useMemo(() => {
    const out: Record<string, typeof demo.contexts> = {};
    for (const c of demo.contexts) {
      (out[c.category] ||= []).push(c);
    }
    return out;
  }, [demo.contexts]);

  // Kill switch / availability: hide the widget entirely when disabled.
  if (!demo.ready) {
    return (
      <div className={className}>
        <div className="h-40 rounded-2xl border border-white/10 bg-white/5 animate-pulse" />
      </div>
    );
  }
  if (!demo.enabled) return null;

  const hasContext = Boolean(presetKey) || freeText.trim().length > 0;
  const inCall = demo.phase === 'connecting' || demo.phase === 'active' || demo.phase === 'ended';
  const presetOrNull = presetKey || null;
  const textOrNull = freeText.trim() ? freeText.trim() : null;

  // Reset the hook AND the widget's own form fields, so a freshly-presented
  // form never shows a pre-filled number or a pre-ticked consent box.
  const handleReset = () => {
    setPresetKey('');
    setFreeText('');
    setPhone('');
    setConsent(false);
    demo.reset();
  };

  const onStart = () => {
    if (channel === 'web') {
      void demo.startWebCall(presetOrNull, textOrNull);
    } else {
      void demo.startPhoneCall(phone.trim(), consent, presetOrNull, textOrNull);
    }
  };

  const startDisabled =
    !hasContext
    || demo.phase === 'connecting'
    || demo.phase === 'phone_submitting'
    || (channel === 'phone' && (!phone.trim() || !consent));

  return (
    <div
      className={`w-full max-w-md rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/60 to-slate-900/70 backdrop-blur-xl shadow-2xl p-5 sm:p-6 text-white ${className ?? ''}`}
    >
      <div className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-300">
          Live demo
        </p>
        <h3 className="mt-1 text-2xl font-bold">Get called by our AI now</h3>
        <p className="mt-1 text-sm text-gray-300">
          Pick a scenario, and our AI agent calls you — on your phone or right here in
          your browser. It only takes a few seconds.
        </p>
      </div>

      {/* Active WebRTC call surface */}
      {channel === 'web' && inCall ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-center">
          {demo.phase === 'connecting' && (
            <p className="text-sm text-gray-300">Connecting you to the agent…</p>
          )}
          {demo.phase === 'active' && (
            <>
              <div className="flex items-center justify-center gap-2 text-cyan-300">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-cyan-400" />
                </span>
                <span className="font-semibold">You're talking with the agent</span>
              </div>
              <p className="mt-2 font-mono text-3xl">{demo.formatDuration(demo.callDuration)}</p>
              {demo.showWarning && demo.secondsRemaining !== null && (
                <p className="mt-1 text-xs text-amber-300">
                  Demo ends in {demo.secondsRemaining}s
                </p>
              )}
              <div className="mt-4 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => void demo.toggleMute()}
                  className="rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10"
                >
                  {demo.isMuted ? 'Unmute' : 'Mute'}
                </button>
                <button
                  type="button"
                  onClick={() => void demo.endCall()}
                  className="rounded-full bg-red-500 px-5 py-2 text-sm font-semibold hover:bg-red-600"
                >
                  End call
                </button>
              </div>
            </>
          )}
          {demo.phase === 'ended' && (
            <>
              <p className="text-sm text-gray-200">Thanks for trying the demo!</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-3 rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white/10"
              >
                Try another scenario
              </button>
            </>
          )}
        </div>
      ) : demo.phase === 'phone_sent' ? (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-5 text-center">
          <PhoneIcon className="mx-auto h-8 w-8 text-emerald-300" />
          <p className="mt-2 text-sm text-gray-100">{demo.info}</p>
          <button
            type="button"
            onClick={demo.reset}
            className="mt-3 rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white/10"
          >
            Done
          </button>
        </div>
      ) : (
        <>
          {/* Channel toggle — compact segmented control */}
          <div className="mb-5 flex justify-center">
            <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-0.5">
              <button
                type="button"
                onClick={() => setChannel('phone')}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  channel === 'phone' ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow' : 'text-gray-300 hover:text-white'
                }`}
              >
                <PhoneIcon className="h-3.5 w-3.5" /> Call my phone
              </button>
              <button
                type="button"
                onClick={() => setChannel('web')}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  channel === 'web' ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow' : 'text-gray-300 hover:text-white'
                }`}
              >
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" /> Talk in browser
              </button>
            </div>
          </div>

          {/* Context picker */}
          <label className="mb-1 block text-sm font-medium text-gray-200">Scenario</label>
          <select
            value={presetKey}
            onChange={(e) => setPresetKey(e.target.value)}
            className="w-full rounded-lg border border-white/15 bg-slate-900/80 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
          >
            <option value="">Choose a scenario…</option>
            {Object.entries(grouped).map(([cat, items]) => (
              <optgroup key={cat} label={CATEGORY_LABELS[cat] || cat}>
                {items.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <label className="mb-1 mt-4 block text-sm font-medium text-gray-200">
            Or describe your own <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value.slice(0, demo.freetextMaxChars))}
            rows={2}
            maxLength={demo.freetextMaxChars}
            placeholder="e.g. Follow up on a solar quote and book a site visit"
            className="w-full resize-none rounded-lg border border-white/15 bg-slate-900/80 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-cyan-400 focus:outline-none"
          />

          {/* PSTN-only fields */}
          {channel === 'phone' && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-200">
                  Your mobile number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={demo.phoneExample || ''}
                  className="w-full rounded-lg border border-white/15 bg-slate-900/80 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-300">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900"
                />
                <span>
                  I agree to receive an automated demo call from Agent Noda on this number.
                </span>
              </label>
            </div>
          )}

          {demo.error && (
            <p className="mt-3 rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-sm text-red-200">
              {demo.error}
            </p>
          )}

          <button
            type="button"
            onClick={onStart}
            disabled={startDisabled}
            className="mt-6 mx-auto flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-7 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
          >
            {channel === 'web' ? (
              <MicrophoneIcon className="h-4 w-4" />
            ) : (
              <PhoneIcon className="h-4 w-4" />
            )}
            {demo.phase === 'connecting' || demo.phase === 'phone_submitting'
              ? 'Starting…'
              : channel === 'web'
                ? 'Start the call'
                : 'Call me now'}
          </button>
          <p className="mt-3 text-center text-xs text-gray-400">
            One short demo call. We only call the number you enter, with your consent.
          </p>
        </>
      )}
    </div>
  );
};

export default DemoCallWidget;
